<template>
  <o-scroll
    id="calendar"
    ref="scroll"
    x-axis
    y-axis
    :allow-drag="scrollable"
    :events="scrollEventHandlers"
    class="h-full w-full relative"
    container-class="h-auto w-max capture"
    @scroll="handleScroll"
  >
    <transition name="fade">
      <div v-if="isActive" class="fixed h-screen w-screen inset-0 bg-white/50" />
    </transition>

    <!--<div class="p-3 rounded-md bg-black/75 text-white text-sm fixed top-0 right-0 m-6 z-80000">
      <p>Viewport height: {{ viewport.height }}</p>
      <p>Viewport offset: {{ viewport.offset }}</p>
    </div>-->

    <!-- Headers -->
    <div class="flex sticky top-0 bg-white dark:bg-gray-800 z-60 min-w-full h-14">
      <transition name="fade">
        <div v-if="isActive" class="absolute h-full w-full inset-0 bg-white/50 dark:bg-gray-900 z-70" />
      </transition>
      <div class="flex-shrink-0 z-70 border-b border-r border-gray-100 dark:border-gray-800 sticky left-0 bg-white dark:bg-gray-900 w-16" />
      <div
        v-for="(section, index) in sections"
        :key="`section-${index}`"
        class="flex-shrink-0 bg-white dark:bg-gray-900 border-b border-r border-gray-100 dark:border-gray-800 z-60"
        :style="{ width: `${sectionSize}px` }"
      >
        <slot
          name="header"
          :section="section"
        />
      </div>
    </div>

    <div v-if="hasSections" class="sticky left-0 border-r border-gray-100 dark:border-gray-800 z-50 bg-white dark:bg-gray-900 w-16 overflow-y-hidden min-h-full" :style="{ height: (isActive ? `${minuteSize * 1440}px` : `${viewport.height}px`) }">
      <div class="w-full absolute" :style="{ marginTop: (isActive ? '0' : `${viewport.offset}px`) }">
        <schedule-marker :provider="provider" pill />

        <slot name="labels">
          <div>
            <div
              v-for="(item, index) in times"
              :key="item"
              :class="[
                'pb-4 border-gray-100 dark:border-gray-800 text-right font-medium text-xs md:text-sm pr-2'
              ]"
              :style="{height: (minuteSize * slotSize) + 'px'}"
            >
              <div
                v-if="index"
                class="transform -translate-y-1/2 opacity-50 p-1 rounded absolute right-0"
                :class="[
                  { 'bg-primary-500/20 text-primary-600 dark:text-primary-400': index === hoveredTimeIndex }
                ]"
              >
                <span v-if="item % 60 === 0" class="font-semibold">
                  {{ $date.minutes(item, timeFormat) }}
                </span>

                <span v-else class="text-[0.7rem] opacity-85">
                  {{ $date.minutes(item, 'mm') }}
                </span>
              </div>
            </div>
          </div>
        </slot>
      </div>
    </div>

    <!-- Columns -->
    <div :style="{ height: (isActive ? `${minuteSize * 1440}px` : `${viewport.height}px`) }" class="min-h-full absolute top-0 w-max min-w-full ml-16 mt-14 overflow-y-clip">
      <div
        v-if="hasSections"
        class="absolute flex w-auto min-w-full"
        :style="{ marginTop: (isActive ? '0' : `${viewport.offset}px`) }"
        @mousemove="handleMouseMove"
        @mouseleave="handleMouseLeave"
      >
        <schedule-marker :provider="provider" />
        <!-- Content -->
        <div class="relative flex-grow flex-shrink select-none">
          <slot name="content-dynamic" />
          <schedule-modifier
            ref="modifier"
            :provider="provider"
            :sections="sections"
            v-model:active="active"
          >
            <schedule-section
              v-for="(section, index) in sections"
              :key="index"
              :section="section"
              :provider="provider"
            />
          </schedule-modifier>
        </div>
      </div>
    </div>

    <div v-if="!hasSections && !loading" class="absolute inset-0 h-full w-full flex flex-col items-center justify-center z-60">
      <img src="~/assets/images/illustrations/empty-schedule.svg?url" class="max-h-xs max-w-xs">
      <h1 class="text-3xl font-bold mt-12 mb-3">
        {{ $t('components.schedule.elements.container.noEmployees') }}
      </h1>
      <p class="opacity-70 mb-10">
        {{ $t('components.schedule.elements.container.noEmployeesInfo') }}
      </p>
      <o-button to="/roster" variant="success" size="lg">
        {{ $t('roster.edit') }}
      </o-button>
    </div>
  </o-scroll>
</template>

<script setup lang="ts">
import ScheduleModifier from '@/components/schedule/elements/Modifier.vue'
import ScheduleSection from '@/components/schedule/elements/Section.vue'
import ScheduleMarker from '@/components/schedule/elements/Marker.vue'

import { TimeFormat } from '@/_api/models/location'

import { useUIStore } from '~/stores/useUIStore'

const { $auth } = useNuxtApp()
const uiStore = useUIStore()

interface Props {
  provider: any
  loading?: boolean
}

const props = withDefaults(defineProps<Props>(), {
  loading: false,
})

const scroll = ref<any>()
const modifier = ref<any>()

const sections = ref<any>(null)
const scrollable = ref(true)
const active = ref<any>(null)
const scrolling = ref(false)
const viewport = reactive({
  offset: null as number | null,
  height: null as number | null,
})
const hoveredTimeIndex = ref<number | null>(null)

let interval: ReturnType<typeof setInterval> | null = null
let size: any = null

const timeFormat = computed(() => {
  const format = $auth?.info?.location?.timeFormat

  if (format && format === TimeFormat.twentyFourHour) {
    return 'HH:mm'
  }

  return 'h a'
})

const minuteSize = computed(() => {
  const value = Number($auth?.info?.minuteSize)
  return Number.isFinite(value) && value > 0 ? value : 2
})
const slotSize = computed(() => {
  const value = Number($auth?.info?.slotSize)
  return Number.isFinite(value) && value > 0 ? value : 15
})
const sectionSize = computed(() => uiStore.schedule.sectionSize)
const showResources = computed(() => uiStore.schedule.showResources)

const times = computed(() => {
  const start = 0
  const finish = 1395
  const s = slotSize.value
  const times: number[] = []

  for (let i = start; i < (finish + s * 3); i = i + s) {
    times.push(i)
  }

  return times
})

const scrollEventHandlers = computed(() => ({}))

const isActive = computed(() => !!active.value?.item)
const hasSections = computed(() => sections.value && Object.values(sections.value).length > 0)

function updateSections() {
  const provider = props.provider
  sections.value = provider?.sections ? provider.sections({ showResources: showResources.value }) : null
}

watch(() => props.provider, () => {
  updateSections()
  setViewport()
})

watch(minuteSize, () => {
  updateSections()
  setViewport()
})

watch(isActive, (value) => {
  const scrollRef = scroll.value
  if (!scrollRef) return

  if (value) {
    // Block drag started: content shifts from viewport.offset to 0
    // Compensate scroll position so the view doesn't jump
    const pos = scrollRef.position()
    const current = pos?.y ?? 0
    const offset = Math.abs(viewport.offset ?? 0)
    const y = current + offset

    nextTick(() => {
      scrollRef.scroll({ y })
    })
  } else {
    // Block drag ended: restore viewport and scroll to top
    setTimeout(() => {
      setViewport()
      nextTick(() => {
        scrollRef.scroll({ y: 0 })
      })
    }, 500)
  }
})

watch(showResources, () => {
  updateSections()
})

onMounted(() => {
  updateSections()
  setViewport()
})

function handleScroll(s: boolean) {
  scrolling.value = s
}

function handleMouseMove(event: MouseEvent) {
  const activeVal = active.value

  const gridEl = modifier.value?.$el
  if (gridEl) {
    const gridOffset = gridEl.getBoundingClientRect()
    const offsetY = event.clientY - gridOffset.top
    const slotHeight = minuteSize.value * slotSize.value
    hoveredTimeIndex.value = Math.floor(offsetY / slotHeight)
  }

  if (!activeVal?.move && !activeVal?.resize) {
    return
  }

  const parent = document.getElementById('calendar')
  const offset = parent!.getBoundingClientRect()

  const { width, height } = offset

  const offsetLeft = event.pageX - offset.left
  const offsetRight = width - offsetLeft
  const offsetTop = event.pageY - offset.top
  const offsetBottom = height - offsetTop

  let distance = 40

  if (offsetBottom < 40) {
    move({ axis: 'y', distance })
  } else if (offsetTop < 70) {
    distance = -Math.abs(distance)
    move({ axis: 'y', distance })
  } else if (offsetLeft < 20) {
    distance = -Math.abs(distance)
    move({ axis: 'x', distance })
  } else if (offsetRight < 20) {
    move({ axis: 'x', distance })
  } else {
    if (interval) clearInterval(interval)
  }
}

function handleMouseLeave() {
  if (interval) clearInterval(interval)
  hoveredTimeIndex.value = null
}

function move({ axis, distance }: { axis: string; distance: number }) {
  if (interval) clearInterval(interval)

  interval = setInterval(() => {
    if (!active.value?.move && !active.value?.resize) {
      if (interval) clearInterval(interval)
    } else {
      const pos = scroll.value?.position()
      if (!pos) return

      const current = axis === 'y' ? pos.y : pos.x
      const target = current + distance

      scroll.value.scroll(axis === 'y' ? { y: target } : { x: target })

      if (axis === 'y') {
        setViewport()
        modifier.value?.scroll(distance)
      }
    }
  }, 300)
}

function reset() {
  props.provider?.reset?.()
  updateSections()
}

function setViewport() {
  setTimeout(() => {
    const provider = props.provider
    if (!provider?.range) {
      return
    }

    const range = provider.range(minuteSize.value)
    if (!range) {
      return
    }

    const start = range.startTime
    const finish = range.finishTime

    const slotSizeVal = slotSize.value * minuteSize.value

    viewport.height = finish - start + (slotSizeVal * 2)
    viewport.offset = -Math.abs(start - slotSizeVal)

    scroll.value?.update()
  }, 100)
}

provide('container', reactive({
  scrollable,
  scrolling,
  reset,
}))
</script>
